import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';
import { RoutePath, routeAccess } from 'src/router';

import { useSelector } from 'src/store';
import {
  isLoggedInSelector,
  authUserIdSelector,
  authRoleSelector,
  authRoleListSelector,
} from 'src/store/selectors/authSelector';
import {
  tenantBrandingLogoUrlSelector,
  tenantBrandingLogoAltSelector,
  tenantBrandingNameSelector,
} from 'src/store/selectors/tenantBrandingSelector';
import {
  isOneCompanyUserSelector,
  hasDefaultPolicySelector,
  selectedCompanyIdSelector,
  selectedCompanyNameSelector,
  isViewConfigurationCompanySelector,
} from 'src/store/selectors/companySelector';
import {
  selectedMembershipIdSelector,
  selectedMembershipIsViewSelector,
  selectedPensionRefSelector,
} from 'src/store/selectors/membershipSelector';

import { MenuIcon, PensionFusionIcon, HideIcon } from '@itm/shared-frontend/lib/components/icons';
import { Navigation, LinkWithParams, NavigationLocalConfig } from '@itm/shared-frontend/lib/components';

import CompanyFilter from 'src/components/CompanyFilter';

import { clientPortalDomain, clientPortalDashboardUrl, isAzureTargetPlatform } from 'src/utils/constants';

import { getUserPermissionsByCompany } from 'src/api/clientPortal/permissions';

import { Role, GlobalRole, UserPermissionResponse } from 'src/types';

import styles from './Sidebar.module.scss';

type Props = Readonly<{
  isActive: boolean;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
}>;

function Sidebar({ isActive, setIsActive }: Props) {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const userId = useSelector(authUserIdSelector);
  const userRole = useSelector(authRoleSelector);
  const userRoleListFromToken = useSelector(authRoleListSelector);
  const brandingLogoUrl = useSelector(tenantBrandingLogoUrlSelector);
  const brandingLogoAlt = useSelector(tenantBrandingLogoAltSelector);
  const brandingName = useSelector(tenantBrandingNameSelector);

  const isOneCompanyUser = useSelector(isOneCompanyUserSelector);
  const selectedCompanyId = useSelector(selectedCompanyIdSelector);
  const selectedCompanyName = useSelector(selectedCompanyNameSelector);
  const isViewConfigurationCompany = useSelector(isViewConfigurationCompanySelector);
  const hasDefaultPolicy = useSelector(hasDefaultPolicySelector);

  const selectedMembershipId = useSelector(selectedMembershipIdSelector);
  const selectedPensionRef = useSelector(selectedPensionRefSelector);
  const selectedMembershipIsView = useSelector(selectedMembershipIsViewSelector);

  const [userPermissionList, setUserPermissionList] = useState<UserPermissionResponse[]>([]);

  const userRoleList = useMemo<GlobalRole[]>(() => {
    if (!userRole) return [];
    if (userRole === Role.SuperAdmin || userRole === Role.Support) return [userRole] as unknown as GlobalRole[];

    const clientPortalRole = userRoleListFromToken.find((role) => role.startsWith('ClientPortal'));
    const productRolesByCompany = [...new Set(userPermissionList.map(({ productRoleName }) => productRoleName))];

    return clientPortalRole ? [clientPortalRole, ...productRolesByCompany] : productRolesByCompany;
  }, [userPermissionList, userRole, userRoleListFromToken]);

  const fetchUserPermissionList = useCallback(async () => {
    if (!userId || !selectedCompanyId || userRole === Role.SuperAdmin || userRole === Role.Support) {
      setUserPermissionList([]);
      return;
    }
    try {
      const res = await getUserPermissionsByCompany(userId, selectedCompanyId);
      setUserPermissionList(res.data);
    } catch {
      console.warn('User Permissions unavailable');
    }
  }, [userId, selectedCompanyId, userRole]);

  const isViewConfigurationMode = userRole === Role.Viewer || isViewConfigurationCompany;

  const navigationLocalConfig = useMemo<NavigationLocalConfig<Role>>(
    () => [
      {
        name: 'All Products',
        section: 'products',
        path: clientPortalDashboardUrl,
        icon: () => <MenuIcon />,
      },
      {
        name: 'Pension Fusion',
        section: 'products',
        path: RoutePath.root,
        isInternalRoute: true,
        icon: () => <PensionFusionIcon />,
        children: [
          {
            name: 'Configuration',
            path: RoutePath.configurationRoot,
            role: { allowList: routeAccess.configurationRoot },
            children: [
              {
                name: isOneCompanyUser ? null : 'General Settings',
                path: RoutePath.configurationGeneralSettingsList,
                role: { allowList: routeAccess.configurationGeneralSettingsList },
              },
              // Configuration Company
              {
                name: selectedCompanyName,
                path: isViewConfigurationMode
                  ? RoutePath.configurationGeneralSettingsViewRoot
                  : RoutePath.configurationGeneralSettingsEditRoot,
                role: { allowList: routeAccess.configurationGeneralSettingsEditRoot },
                children: [
                  {
                    name: 'PDP connection',
                    path: isViewConfigurationMode
                      ? RoutePath.configurationGeneralSettingsViewPDPConnectionRoot
                      : RoutePath.configurationGeneralSettingsEditPDPConnectionRoot,
                    role: { allowList: routeAccess.configurationGeneralSettingsEditPDPConnectionRoot },
                  },
                  {
                    name: 'Additional options',
                    path: isViewConfigurationMode
                      ? RoutePath.configurationGeneralSettingsViewAdditionalOptions
                      : RoutePath.configurationGeneralSettingsEditAdditionalOptions,
                    role: { allowList: routeAccess.configurationGeneralSettingsEditAdditionalOptions },
                  },
                  {
                    name: 'Complex matching',
                    path: isViewConfigurationMode
                      ? RoutePath.configurationGeneralSettingsViewComplexMatchingRoot
                      : RoutePath.configurationGeneralSettingsEditComplexMatchingRoot,
                    role: { allowList: routeAccess.configurationGeneralSettingsEditComplexMatchingRoot },
                  },
                  {
                    name: 'PGP Key Configuration',
                    path: isViewConfigurationMode
                      ? RoutePath.configurationGeneralSettingsViewPGPKeyConfigurationRoot
                      : RoutePath.configurationGeneralSettingsEditPGPKeyConfigurationRoot,
                    role: { allowList: routeAccess.configurationGeneralSettingsEditPGPKeyConfigurationRoot },
                  },
                ],
              },
              // Configuration Matching
              {
                name: 'Matching',
                path: RoutePath.configurationMatchingRoot,
                role: { allowList: routeAccess.configurationMatchingRoot },
                children: [
                  {
                    name: 'All Policies',
                    path: RoutePath.configurationMatchingPolicyMain,
                    role: { allowList: routeAccess.configurationMatchingPolicyMain },
                  },
                  {
                    name: hasDefaultPolicy ? null : 'Create New Policy',
                    path: RoutePath.configurationMatchingPolicyCreateRoot,
                    role: { allowList: routeAccess.configurationMatchingPolicyCreateRoot },
                  },
                  {
                    name: 'Matching Test',
                    path: RoutePath.configurationMatchingTestRoot,
                    role: { allowList: routeAccess.configurationMatchingTestRoot },
                  },
                ],
              },
              // Configuration Publish
              {
                name: 'Publish Configuration',
                path: RoutePath.configurationPublishConfigurationRoot,
                role: { allowList: routeAccess.configurationPublishConfigurationRoot },
                children: [
                  {
                    name: 'Publish Changes',
                    path: RoutePath.configurationPublishConfigurationPublishChanges,
                  },
                  {
                    name: 'Audit',
                    path: RoutePath.configurationPublishConfigurationAuditRoot,
                  },
                ],
              },
              // Configuration PDP Audit
              {
                name: 'PDP Audit',
                path: RoutePath.configurationPDPAuditRoot,
                role: { allowList: routeAccess.configurationPDPAuditRoot },
              },
              // Configuration PDP Synchronisation Management
              {
                name: 'PDP Synchronisation Management',
                path: RoutePath.configurationPDPSynchronisationManagementRoot,
                role: { allowList: routeAccess.configurationPDPSynchronisationManagementRoot },
              },
            ],
          },
          {
            name: 'Data Management',
            path: RoutePath.dataManagementRoot,
            role: { allowList: routeAccess.dataManagementRoot },
            children: [
              {
                name: 'Data Upload History',
                path: RoutePath.dataManagementHistoryRoot,
                role: { allowList: routeAccess.dataManagementHistoryRoot },
              },
              {
                name: 'Upload Source Data',
                path: RoutePath.dataManagementUploadSourceData,
                role: { allowList: routeAccess.dataManagementUploadSourceData },
              },
              {
                name: 'Member Search',
                path: RoutePath.dataManagementMemberList,
              },
              {
                name: 'Audit',
                path: RoutePath.dataManagementAuditRoot,
              },
              {
                name: selectedMembershipIsView === null ? null : selectedPensionRef,
                path: selectedMembershipIsView
                  ? generatePath(RoutePath.dataManagementMemberViewRoot, { membershipId: selectedMembershipId })
                  : generatePath(RoutePath.dataManagementMemberEditRoot, { membershipId: selectedMembershipId }),
                children: [
                  {
                    name: 'Membership Detail',
                    path: selectedMembershipIsView
                      ? generatePath(RoutePath.dataManagementMemberViewDetailRoot, {
                          membershipId: selectedMembershipId,
                        })
                      : generatePath(RoutePath.dataManagementMemberEditDetailRoot, {
                          membershipId: selectedMembershipId,
                        }),
                  },
                  {
                    name: 'Pension Value Detail',
                    path: selectedMembershipIsView
                      ? generatePath(RoutePath.dataManagementMemberViewPensionValueRoot, {
                          membershipId: selectedMembershipId,
                        })
                      : generatePath(RoutePath.dataManagementMemberEditPensionValueRoot, {
                          membershipId: selectedMembershipId,
                        }),
                  },
                  {
                    name: 'Matching Details',
                    path: selectedMembershipIsView
                      ? generatePath(RoutePath.dataManagementMemberViewMatchingDetailsRoot, {
                          membershipId: selectedMembershipId,
                        })
                      : generatePath(RoutePath.dataManagementMemberEditMatchingDetailsRoot, {
                          membershipId: selectedMembershipId,
                        }),
                  },
                  {
                    name: 'Audit',
                    path: selectedMembershipIsView
                      ? generatePath(RoutePath.dataManagementMemberViewAuditRoot, {
                          membershipId: selectedMembershipId,
                        })
                      : generatePath(RoutePath.dataManagementMemberEditAuditRoot, {
                          membershipId: selectedMembershipId,
                        }),
                  },
                ],
              },
            ],
          },
          {
            name: 'Analytics & Reports',
            path: RoutePath.analyticsRoot,
            role: { allowList: routeAccess.analyticsRoot },
            children: [
              {
                name: 'Pension Fusion Insights',
                path: RoutePath.analyticsPensionFusionInsight,
              },
              {
                name: 'Possible Matches',
                path: RoutePath.analyticsPossibleMatches,
                role: { allowList: routeAccess.analyticsPossibleMatches },
              },
              {
                name: 'Pension Values SLA',
                path: RoutePath.analyticsPensionValuesSlaRoot,
                role: { allowList: routeAccess.analyticsPensionValuesSlaRoot },
              },
              {
                name: 'Member Extract',
                path: RoutePath.analyticsMemberExtract,
                role: { allowList: routeAccess.analyticsMemberExtract },
              },
              {
                name: 'Dashboards Log Data',
                path: RoutePath.analyticsPensionLogs,
                role: { allowList: routeAccess.analyticsPensionLogs },
              },
              {
                name: 'Audit',
                path: RoutePath.analyticsExtractAuditRoot,
                role: { allowList: routeAccess.analyticsExtractAuditRoot },
              },
            ],
          },
          {
            name: 'Product Information',
            path: RoutePath.productInfo,
            role: { allowList: routeAccess.productInfo },
          },
        ],
      },
    ],
    [
      isOneCompanyUser,
      selectedCompanyName,
      isViewConfigurationMode,
      hasDefaultPolicy,
      selectedMembershipIsView,
      selectedPensionRef,
      selectedMembershipId,
    ],
  );

  useEffect(() => {
    fetchUserPermissionList();
  }, [fetchUserPermissionList]);

  return (
    <aside
      className={classNames([
        styles.Wrapper,
        'is-flex is-flex-direction-column has-background-white is-relative',
        { [styles.IsActive]: isActive },
      ])}
      id="sidebar"
    >
      <header className={classNames(styles.Header, 'is-flex-shrink-0')}>
        <div className="columns is-align-items-center is-gapless mb-0">
          <div className="column is-narrow">
            <LinkWithParams className={classNames(styles.Logo, 'has-icon')} to={RoutePath.root}>
              <img src={brandingLogoUrl} alt={brandingLogoAlt} />
            </LinkWithParams>
          </div>
          {isActive && (
            <div className="column">
              <h2 className="title is-nowrap is-4 ml-4">{brandingName}</h2>
            </div>
          )}
        </div>
        {isActive && <CompanyFilter />}
      </header>
      {isLoggedIn && (
        <>
          <button
            className={classNames(styles.ToggleButton, 'has-icon', { 'is-active': isActive })}
            type="button"
            onClick={() => setIsActive(!isActive)}
            aria-controls="sidebar"
            aria-label={isActive ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <span className="icon">
              <HideIcon />
            </span>
          </button>
          {userRole && (
            <Navigation
              isActiveSidebar={isActive}
              clientPortalDomain={clientPortalDomain}
              localConfig={navigationLocalConfig}
              userRoleList={userRoleList}
              isAzureTargetPlatform={isAzureTargetPlatform}
              isCommonItemsCollapse
            />
          )}
        </>
      )}
    </aside>
  );
}

export default Sidebar;
